/**
 * Mock Certificate Data
 * Contains sample halal certificates and training certificates
 */

import type { TrainingCertificate } from '../types/training';
import type {
  Certificate,
  CertificateStatus,
  CertificateType,
  CertificateStatistics,
  CertificateVerificationResult,
} from '../types/certificate';

/**
 * Mock Halal Certificates
 */
export const mockCertificates: Certificate[] = [
  {
    id: 'cert-001',
    certificateNumber: 'HAL/2025/FP/001234',
    companyId: 'org-001',
    companyName: 'Nasi Lemak Sedap Sdn Bhd',
    applicationId: 'app-001',
    type: 'FOOD_PREMISE',
    status: 'ACTIVE',
    issueDate: '2025-01-01',
    expiryDate: '2026-12-31',
    issuedBy: 'JAKIM',
    issuedByOfficer: 'Ahmad Fauzi bin Hassan',
    productCategory: 'Food Premise',
    scope: 'Restaurant and food preparation for Malaysian cuisine including nasi lemak, rendang, and satay',
    address: '123 Jalan Halal, Taman Industri, 47100 Puchong, Selangor',
    verificationCode: 'HAL2025FP001234VERIFY',
    qrCodeUrl: '/mock/certificates/qr/cert-001.png',
    certificateUrl: '/mock/certificates/pdf/cert-001.pdf',
    remarks: 'Initial certification issued after successful audit',
    createdAt: '2025-01-01T10:00:00Z',
    updatedAt: '2025-01-01T10:00:00Z',
  },
  {
    id: 'cert-002',
    certificateNumber: 'HAL/2024/FP/005678',
    companyId: 'org-002',
    companyName: 'Bakery Delight Sdn Bhd',
    applicationId: 'app-005',
    type: 'FOOD_PRODUCT',
    status: 'ACTIVE',
    issueDate: '2024-06-15',
    expiryDate: '2026-06-14',
    issuedBy: 'JAKIM',
    issuedByOfficer: 'Nurul Huda binti Abdullah',
    productCategory: 'Bakery Products',
    scope: 'Production of halal bread, cakes, pastries, and biscuits',
    address: '456 Jalan Bakery, Kawasan Perindustrian, 43000 Kajang, Selangor',
    verificationCode: 'HAL2024FP005678VERIFY',
    qrCodeUrl: '/mock/certificates/qr/cert-002.png',
    certificateUrl: '/mock/certificates/pdf/cert-002.pdf',
    createdAt: '2024-06-15T14:30:00Z',
    updatedAt: '2024-06-15T14:30:00Z',
  },
  {
    id: 'cert-003',
    certificateNumber: 'HAL/2023/SH/000123',
    companyId: 'org-003',
    companyName: 'Premium Meat Supply Sdn Bhd',
    applicationId: 'app-010',
    type: 'SLAUGHTERHOUSE',
    status: 'EXPIRED',
    issueDate: '2023-03-01',
    expiryDate: '2025-02-28',
    issuedBy: 'JAKIM',
    issuedByOfficer: 'Muhammad Amin bin Ismail',
    productCategory: 'Slaughterhouse',
    scope: 'Halal slaughtering of poultry and livestock according to Islamic requirements',
    address: '789 Jalan Industri, Taman Peternakan, 71000 Port Dickson, Negeri Sembilan',
    verificationCode: 'HAL2023SH000123VERIFY',
    qrCodeUrl: '/mock/certificates/qr/cert-003.png',
    certificateUrl: '/mock/certificates/pdf/cert-003.pdf',
    remarks: 'Certificate expired, renewal application in progress',
    createdAt: '2023-03-01T09:00:00Z',
    updatedAt: '2025-02-28T23:59:59Z',
  },
  {
    id: 'cert-004',
    certificateNumber: 'HAL/2024/LOG/002456',
    companyId: 'org-004',
    companyName: 'Fast Logistics Express Sdn Bhd',
    applicationId: 'app-012',
    type: 'LOGISTICS',
    status: 'ACTIVE',
    issueDate: '2024-09-01',
    expiryDate: '2026-08-31',
    issuedBy: 'JAKIM',
    issuedByOfficer: 'Fatimah binti Mohamed',
    productCategory: 'Logistics & Transportation',
    scope: 'Transportation and storage of halal food products with dedicated halal-only vehicles',
    address: '321 Jalan Logistik, Taman Perindustrian Besar, 81100 Johor Bahru, Johor',
    verificationCode: 'HAL2024LOG002456VERIFY',
    qrCodeUrl: '/mock/certificates/qr/cert-004.png',
    certificateUrl: '/mock/certificates/pdf/cert-004.pdf',
    createdAt: '2024-09-01T11:20:00Z',
    updatedAt: '2024-09-01T11:20:00Z',
  },
  {
    id: 'cert-005',
    certificateNumber: 'HAL/2024/REST/003789',
    companyId: 'org-005',
    companyName: 'The Halal Bistro',
    applicationId: 'app-015',
    type: 'RESTAURANT',
    status: 'ACTIVE',
    issueDate: '2024-11-15',
    expiryDate: '2026-11-14',
    issuedBy: 'JAKIM',
    issuedByOfficer: 'Zulkifli bin Rahman',
    productCategory: 'Restaurant',
    scope: 'Full-service halal restaurant serving Western and fusion cuisine',
    address: '555 Jalan Makan, Pusat Bandar, 50050 Kuala Lumpur',
    verificationCode: 'HAL2024REST003789VERIFY',
    qrCodeUrl: '/mock/certificates/qr/cert-005.png',
    certificateUrl: '/mock/certificates/pdf/cert-005.pdf',
    createdAt: '2024-11-15T16:45:00Z',
    updatedAt: '2024-11-15T16:45:00Z',
  },
  {
    id: 'cert-006',
    certificateNumber: 'HAL/2024/FPR/004567',
    companyId: 'org-006',
    companyName: 'Catering Excellence Sdn Bhd',
    applicationId: 'app-018',
    type: 'FOOD_PREPARATION',
    status: 'SUSPENDED',
    issueDate: '2024-05-01',
    expiryDate: '2026-04-30',
    issuedBy: 'JAKIM',
    issuedByOfficer: 'Haslinda binti Yaacob',
    productCategory: 'Food Preparation & Catering',
    scope: 'Large-scale halal catering services for events and corporate functions',
    address: '888 Jalan Katering, Kawasan Komersial, 40000 Shah Alam, Selangor',
    verificationCode: 'HAL2024FPR004567VERIFY',
    qrCodeUrl: '/mock/certificates/qr/cert-006.png',
    certificateUrl: '/mock/certificates/pdf/cert-006.pdf',
    remarks: 'Temporarily suspended pending investigation of non-compliance issues',
    createdAt: '2024-05-01T10:30:00Z',
    updatedAt: '2024-12-20T14:00:00Z',
  },
  {
    id: 'cert-007',
    certificateNumber: 'HAL/2022/FP/009876',
    companyId: 'org-007',
    companyName: 'Spice World Manufacturing',
    applicationId: 'app-020',
    type: 'FOOD_PRODUCT',
    status: 'REVOKED',
    issueDate: '2022-08-10',
    expiryDate: '2024-08-09',
    issuedBy: 'JAKIM',
    issuedByOfficer: 'Abdul Razak bin Yusof',
    productCategory: 'Spices & Condiments',
    scope: 'Manufacturing and packaging of halal spices, seasonings, and condiments',
    address: '999 Jalan Rempah, Taman Industri Kecil, 31200 Chemor, Perak',
    verificationCode: 'HAL2022FP009876VERIFY',
    qrCodeUrl: '/mock/certificates/qr/cert-007.png',
    certificateUrl: '/mock/certificates/pdf/cert-007.pdf',
    remarks: 'Certificate revoked due to serious violations of halal standards',
    revokedAt: '2024-03-15T09:00:00Z',
    revokedBy: 'Ahmad Fauzi bin Hassan',
    revokedReason: 'Use of non-halal certified ingredients without proper documentation',
    createdAt: '2022-08-10T13:15:00Z',
    updatedAt: '2024-03-15T09:00:00Z',
  },
];

/**
 * Mock Training Certificates
 */
export const mockTrainingCertificates: TrainingCertificate[] = [
  {
    id: 'cert-train-001',
    certificateNumber: 'HEC/AWARE/2025/0045',
    courseId: 'course-001',
    courseName: 'Halal Awareness Training',
    userId: 'user-001',
    userName: 'Ahmad bin Abdullah',
    issuedAt: new Date('2025-12-16'),
    issuedBy: 'Halal Excellence Consultancy',
    expiryDate: new Date('2027-12-16'),
    score: 88,
    certificateUrl: '/mock/certificates/halal-awareness-ahmad-2025.pdf',
    createdAt: new Date('2025-12-16'),
  },
  {
    id: 'cert-train-002',
    certificateNumber: 'HEC/EXEC/2025/0012',
    courseId: 'course-002',
    courseName: 'Halal Executive Training',
    userId: 'user-001',
    userName: 'Ahmad bin Abdullah',
    issuedAt: new Date('2025-06-20'),
    issuedBy: 'Halal Excellence Consultancy',
    expiryDate: new Date('2028-06-20'),
    score: 92,
    certificateUrl: '/mock/certificates/halal-executive-ahmad-2025.pdf',
    createdAt: new Date('2025-06-20'),
  },
  {
    id: 'cert-train-003',
    certificateNumber: 'HEC/SUPER/2025/0034',
    courseId: 'course-003',
    courseName: 'Halal Supervisor Training',
    userId: 'user-002',
    userName: 'Nurul Aisyah',
    issuedAt: new Date('2025-08-15'),
    issuedBy: 'Halal Excellence Consultancy',
    expiryDate: new Date('2027-08-15'),
    score: 85,
    certificateUrl: '/mock/certificates/halal-supervisor-nurul-2025.pdf',
    createdAt: new Date('2025-08-15'),
  },
];

/**
 * Get certificate by ID
 */
export function getCertificateById(id: string): Certificate | undefined {
  return mockCertificates.find((cert) => cert.id === id);
}

/**
 * Get certificate by certificate number
 */
export function getCertificateByCertNumber(certNumber: string): Certificate | undefined {
  return mockCertificates.find((cert) => cert.certificateNumber === certNumber);
}

/**
 * Get certificate by verification code
 */
export function getCertificateByVerificationCode(code: string): Certificate | undefined {
  return mockCertificates.find((cert) => cert.verificationCode === code);
}

/**
 * Get certificates by company ID
 */
export function getCertificatesByCompany(companyId: string): Certificate[] {
  return mockCertificates.filter((cert) => cert.companyId === companyId);
}

/**
 * Get certificates by status
 */
export function getCertificatesByStatus(status: CertificateStatus): Certificate[] {
  return mockCertificates.filter((cert) => cert.status === status);
}

/**
 * Get active certificates
 */
export function getActiveCertificates(): Certificate[] {
  return getCertificatesByStatus('ACTIVE');
}

/**
 * Get expiring certificates (within 90 days)
 */
export function getExpiringCertificates(daysThreshold: number = 90): Certificate[] {
  const now = new Date();
  const thresholdDate = new Date(now.getTime() + daysThreshold * 24 * 60 * 60 * 1000);

  return mockCertificates.filter((cert) => {
    if (cert.status !== 'ACTIVE') return false;
    const expiryDate = new Date(cert.expiryDate);
    return expiryDate <= thresholdDate && expiryDate > now;
  });
}

/**
 * Get expired certificates
 */
export function getExpiredCertificates(): Certificate[] {
  const now = new Date();
  return mockCertificates.filter((cert) => {
    const expiryDate = new Date(cert.expiryDate);
    return expiryDate < now || cert.status === 'EXPIRED';
  });
}

/**
 * Verify certificate by certificate number or verification code
 */
export function verifyCertificate(identifier: string): CertificateVerificationResult {
  const verifiedAt = new Date().toISOString();

  let certificate = getCertificateByCertNumber(identifier);
  if (!certificate) {
    certificate = getCertificateByVerificationCode(identifier);
  }

  if (!certificate) {
    return { isValid: false, message: 'Certificate not found', verifiedAt };
  }

  if (certificate.status === 'REVOKED') {
    return {
      isValid: false,
      certificate,
      message: `Certificate has been revoked. Reason: ${certificate.revokedReason || 'Not specified'}`,
      verifiedAt,
    };
  }

  const now = new Date();
  const expiryDate = new Date(certificate.expiryDate);
  if (expiryDate < now || certificate.status === 'EXPIRED') {
    return { isValid: false, certificate, message: 'Certificate has expired', verifiedAt };
  }

  if (certificate.status === 'SUSPENDED') {
    return { isValid: false, certificate, message: 'Certificate is currently suspended', verifiedAt };
  }

  return { isValid: true, certificate, message: 'Certificate is valid and active', verifiedAt };
}

/**
 * Get certificate statistics
 */
export function getCertificateStatistics(): CertificateStatistics {
  const total = mockCertificates.length;
  const active = getActiveCertificates().length;
  const expiringSoon = getExpiringCertificates(90).length;
  const expired = getExpiredCertificates().length;
  const revoked = getCertificatesByStatus('REVOKED').length;

  const byType: Record<CertificateType, number> = {
    FOOD_PREMISE: 0,
    FOOD_PRODUCT: 0,
    SLAUGHTERHOUSE: 0,
    LOGISTICS: 0,
    RESTAURANT: 0,
    FOOD_PREPARATION: 0,
  };

  mockCertificates.forEach((cert) => {
    byType[cert.type] = (byType[cert.type] || 0) + 1;
  });

  const byMonth: { month: string; issued: number }[] = [];
  const now = new Date();

  for (let i = 11; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthStr = date.toISOString().slice(0, 7);

    const issued = mockCertificates.filter((cert) => cert.issueDate.startsWith(monthStr)).length;
    byMonth.push({ month: monthStr, issued });
  }

  return { total, active, expiringSoon, expired, revoked, byType, byMonth };
}

/**
 * Helper to get training certificate by ID
 */
export function getTrainingCertificateById(id: string): TrainingCertificate | undefined {
  return mockTrainingCertificates.find((cert) => cert.id === id);
}

/**
 * Helper to get training certificates by user ID
 */
export function getTrainingCertificatesByUserId(userId: string): TrainingCertificate[] {
  return mockTrainingCertificates.filter((cert) => cert.userId === userId);
}

/**
 * Helper to check if training certificate is valid
 */
export function isTrainingCertificateValid(cert: TrainingCertificate): boolean {
  if (!cert.expiryDate) return true; // No expiry
  return new Date(cert.expiryDate) > new Date();
}

/**
 * Helper to get all certificates (halal + training) count
 */
export function getTotalCertificateCount(): {
  halal: number;
  training: number;
  total: number;
} {
  return {
    halal: mockCertificates.length,
    training: mockTrainingCertificates.length,
    total: mockCertificates.length + mockTrainingCertificates.length,
  };
}
